/**
 * Site-trial billing refusals and their words (PLAN M3, R16, D41; App Server B18 PR 7189, B20 PR 7204).
 *
 * THE ONE PLACE the Dashboard reads a billing refusal off the wire, and the one owner of its copy. The words mirror the
 * Access app (B19, `src/features/billing/billingCopy.ts`) so a customer reads the same sentence on both.
 *
 * Two wire shapes:
 *   WHOLE     HTTP 402 `{ statusCode, message, errorCode, info, details }`: nothing in the request was applied.
 *   PER ITEM  inside a 200: a bulk call applied what it could and names each refused item (`rejected[]` /
 *             `results[]` rows carrying `errorCode`, `errorCodes[]`, `rejectedReason`, `details`).
 *
 * Routed on `errorCode` only, never on the text. A code outside the three gate codes is not a billing refusal here, so
 * it keeps whatever path it had before.
 *
 * Every message says three things, in this order: what is paused (managing access, never the locks), that locks
 * still unlock and existing access keeps working, and who can fix it. Role-aware ONLY by what the server sent:
 * `details.agreementState` is null unless the caller holds Global Admin, Billing Admin or Owner, so its presence is the
 * server saying "this person can act on billing". The Dashboard never reads the person's roles to decide that.
 *
 * Never a price, never "not licensed", never an activation key: a key cannot clear a missing card.
 *
 * Pure: no imports, so superFetch can depend on it without a cycle.
 */

export const BILLING_GATE_CODES = Object.freeze([
	'BILLING_SETUP_REQUIRED',
	'BILLING_PAYMENT_FAILED',
	'BILLING_AGREEMENT_ENDED'
]);

export const isBillingGateCode = (code) => typeof code === 'string' && BILLING_GATE_CODES.includes(code);

export const LOCKS_KEEP_WORKING = 'Locks still unlock and existing access keeps working.';

/** Next step when the copy is not addressed to one caller (the billing page banner). */
export const NEUTRAL_STEP = "The organisation's owner or billing admin can fix this in Zezamii Cloud, under Billing.";

export const BILLING_COPY = Object.freeze({
	BILLING_SETUP_REQUIRED: {
		title: 'Card needed',
		short: 'card needed',
		what: {
			SITE: "This site's grace period has ended and no payment card is saved.",
			AGREEMENT: 'No payment card is saved for this organisation.'
		},
		resumes: 'Adding or changing access here resumes as soon as a card is saved.',
		ownerStep: 'Add a payment card in Zezamii Cloud, under Billing.',
		askStep: "Ask your organisation's owner or billing admin to add a payment card."
	},
	BILLING_PAYMENT_FAILED: {
		title: 'Payment needs attention',
		short: 'payment needs attention',
		what: {
			SITE: "A payment for this site didn't go through.",
			AGREEMENT: "The renewal payment didn't go through."
		},
		resumes: 'Adding or changing access here resumes once the card is updated.',
		ownerStep: 'Update the payment card in Zezamii Cloud, under Billing.',
		askStep: "Ask your organisation's owner or billing admin to update the payment card."
	},
	BILLING_AGREEMENT_ENDED: {
		title: 'Billing agreement ended',
		short: 'billing agreement ended',
		what: {
			SITE: "This organisation's billing agreement has ended.",
			AGREEMENT: "This organisation's billing agreement has ended."
		},
		resumes: 'Adding or changing access needs the agreement restarted.',
		ownerStep: 'Restart the agreement in Zezamii Cloud, under Billing, or contact Zezamii.',
		askStep: "Ask your organisation's owner or billing admin to restart it."
	}
});

const str = (v) => (typeof v === 'string' && v.trim() ? v : null);
const num = (v) => {
	const n = typeof v === 'string' && v.trim() ? Number(v) : v;
	return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
};

const detailsFrom = (raw) => {
	const d = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
	return {
		siteID: num(d.siteID),
		reason: str(d.reason),
		scope: d.scope === 'SITE' || d.scope === 'AGREEMENT' ? d.scope : null,
		trialEndsAt: str(d.trialEndsAt),
		agreementState: str(d.agreementState)
	};
};

/**
 * The refusal in a superFetch result, or null. Only a 402 with one of the three codes counts: the same code under
 * another status is not this contract. superFetch returns the parsed body, which carries its own `statusCode`.
 */
export const billingRefusalFromEnvelope = (status, payload) => {
	if (status !== 402 || !payload || typeof payload !== 'object') return null;
	const code = payload.errorCode ?? payload.code;
	if (!isBillingGateCode(code)) return null;
	return { code, message: str(payload.message) ?? str(payload.info), details: detailsFrom(payload.details) };
};

const rowsOf = (v) => (Array.isArray(v) ? v.filter((r) => r && typeof r === 'object') : []);

/**
 * Every billing refusal inside a 2xx body (the body, its `data`, or a bare row array). Rows without a known code are
 * ignored: an unrelated rejection, or a code this build does not know yet.
 */
export const billingItemRefusals = (body) => {
	const out = [];
	const seen = new Set();
	const rowsRead = new Set();
	const readRow = (row) => {
		if (rowsRead.has(row)) return;
		rowsRead.add(row);
		const codes = [
			...(Array.isArray(row.errorCodes) ? row.errorCodes : []),
			row.errorCode,
			row.rejected === true ? row.rejectedReason : undefined
		].filter(isBillingGateCode);
		if (codes.length === 0) return;
		const warnings = Array.isArray(row.warnings) ? row.warnings : [];
		const errors = Array.isArray(row.errors) ? row.errors : [];
		out.push({
			code: codes[0],
			message: str(warnings[warnings.length - 1]) ?? str(errors[0]),
			details: detailsFrom(row.details ?? (row.siteID != null ? { siteID: row.siteID } : undefined)),
			personID: num(row.personID),
			deviceID: num(row.deviceID)
		});
	};
	const visit = (node, depth) => {
		if (depth > 2 || node == null || typeof node !== 'object' || seen.has(node)) return;
		seen.add(node);
		if (Array.isArray(node)) {
			rowsOf(node).forEach(readRow);
			return;
		}
		if (depth === 0 && ('errorCodes' in node || 'errorCode' in node || node.rejected === true)) {
			// A whole envelope carries `errorCode` too; only a row-shaped object (no statusCode) is read as one row.
			if (node.statusCode == null) {
				readRow(node);
				return;
			}
		}
		if (Array.isArray(node.rejected)) rowsOf(node.rejected).forEach(readRow);
		if (Array.isArray(node.results)) rowsOf(node.results).forEach(readRow);
		if (node.data !== undefined) visit(node.data, depth + 1);
	};
	visit(body, 0);
	return out;
};

/** True when the server showed this caller the agreement detail (101/102/115). */
export const serverShowsBillingDetail = (details) => details?.agreementState != null;

const formatDay = (iso) => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Copy for one refusal: `{ code, title, lines[], message }`. `audience: 'neutral'` when the copy is not answering one
 * caller's refused request (the billing page): the status read carries no per-caller step to go on.
 */
export const billingRefusalMessage = (refusal, audience = 'caller') => {
	const copy = BILLING_COPY[refusal.code];
	const scope = refusal.details?.scope === 'AGREEMENT' ? 'AGREEMENT' : 'SITE';
	const owner = audience === 'caller' && serverShowsBillingDetail(refusal.details);
	const endedOn = owner && refusal.details?.trialEndsAt ? formatDay(refusal.details.trialEndsAt) : null;
	const lines = [
		copy.what[scope] +
			(endedOn && refusal.code === 'BILLING_SETUP_REQUIRED' ? ` The grace period ended on ${endedOn}.` : ''),
		LOCKS_KEEP_WORKING,
		copy.resumes,
		audience === 'neutral' ? NEUTRAL_STEP : owner ? copy.ownerStep : copy.askStep
	];
	return { code: refusal.code, title: copy.title, lines, message: lines.join(' ') };
};

const SEVERITY = { BILLING_AGREEMENT_ENDED: 3, BILLING_PAYMENT_FAILED: 2, BILLING_SETUP_REQUIRED: 1 };

/** The refusal to lead with when several apply: ended, then payment, then card. */
export const mostSevere = (refusals) => refusals.reduce((a, b) => (SEVERITY[b.code] > SEVERITY[a.code] ? b : a));

/** Copy for a bulk call where some items were refused for billing and the rest were applied. */
export const billingPartialMessage = (refusals, total) => {
	if (!refusals || refusals.length === 0) return null;
	const first = billingRefusalMessage(mostSevere(refusals));
	const n = refusals.length;
	const head =
		total != null && total > n
			? `${n} of ${total} changes weren't made.`
			: n === 1
				? "That change wasn't made."
				: `${n} changes weren't made.`;
	const lead = `${head} Any other changes were saved.`;
	return { ...first, lines: [lead, ...first.lines], message: `${lead} ${first.message}` };
};

/** Short label for a site's gate on the billing page. `null` gate = management open. */
export const gateLabel = (gate) => (isBillingGateCode(gate) ? BILLING_COPY[gate].title : 'Open');

// ---- B20 customer billing actions (App Server `helpers/billing/customerBillingRefusal.js`) --------------------------

/**
 * Words for a refused cancel / resume / card update, by `errorCode`. `data.reason` refines a few. An unknown code falls
 * back to the server's own message.
 */
const CHANGE_NOT_ALLOWED_REASON = {
	AGREEMENT_ENDED: 'This billing agreement has ended. Restarting it is a new agreement: contact Zezamii.',
	TRIAL_HAS_NOTHING_TO_CANCEL: 'There is nothing to cancel while every site is still in its grace period.',
	ALREADY_CANCEL_SCHEDULED: 'Cancellation is already scheduled for the end of the current period.',
	NOT_CANCEL_SCHEDULED: 'Nothing is scheduled to cancel, so there is nothing to resume.',
	NO_PERIOD_END: 'This agreement has no billing period yet, so there is no date to cancel at.',
	PERIOD_ENDED: 'The current billing period has already ended. Contact Zezamii to change this agreement.'
};

const CARD_UPDATE_REASON = {
	PAYER_NOT_ZEZAMII: 'Your partner handles billing for this organisation, so the card is managed with them.',
	AGREEMENT_ENDED: 'This billing agreement has ended, so there is no card to update.',
	WINDOW_ENDED: 'The time to add a card for this selection has ended. Contact Zezamii.',
	PAYER_RESOLVED: 'The payer for this agreement is already settled. Contact Zezamii to change the card.',
	CARD_ALREADY_SAVED: 'A card is already saved for this agreement.'
};

export const CUSTOMER_BILLING_ERROR_COPY = Object.freeze({
	STB_AGREEMENT_NOT_FOUND: 'This organisation has no per-lock billing agreement.',
	STB_BILLING_AUTHORITY_REQUIRED: "Only the organisation's owner or billing admin can change billing.",
	STB_COMMERCIAL_TERMS_NOT_EDITABLE: 'Plan, price and term cannot be changed here. Contact Zezamii.',
	STB_CHANGE_NOT_ALLOWED: 'That change is not available for this agreement right now.',
	STB_CHANGE_IN_PROGRESS: 'A billing change is already being applied. Try again in a few minutes.',
	STB_OPERATION_ID_REUSED: 'That request was already used for a different change. Refresh and try again.',
	STB_CARD_UPDATE_NOT_AVAILABLE: 'Card update is not available for this agreement.',
	STB_CARD_UPDATE_REFUSED: 'The card update could not be started.',
	STB_CARD_UPDATE_NOT_WIRED: 'Card update is not available yet. Contact Zezamii to update the card.'
});

/** Customer-facing words for a refused B20 action. */
export const customerBillingErrorMessage = (res) => {
	const code = res?.errorCode;
	const reason = res?.data?.reason;
	if (code === 'STB_CHANGE_NOT_ALLOWED' && CHANGE_NOT_ALLOWED_REASON[reason])
		return CHANGE_NOT_ALLOWED_REASON[reason];
	if ((code === 'STB_CARD_UPDATE_NOT_AVAILABLE' || code === 'STB_CARD_UPDATE_REFUSED') && CARD_UPDATE_REASON[reason])
		return CARD_UPDATE_REASON[reason];
	return CUSTOMER_BILLING_ERROR_COPY[code] || str(res?.message) || str(res?.error) || 'That billing change failed.';
};
